export const ACTIVITY_DATA_ITEM_KEY = "activityId";
export const ACTIVITY_SELECTED_FIELD = "selected";

export function getActivitiesToDelete(dataItem, selectedState, listData = []) {
    const selectedIds = Object.keys(selectedState).filter(
        (key) => selectedState[key],
    );
    if (selectedIds.length > 1 && selectedState[dataItem.activityId]) {
        return listData.filter((item) => selectedState[item.activityId]);
    }
    return [dataItem];
}

export function getDeleteConfirmationMessage(items) {
    const itemsArray = Array.isArray(items) ? items : [items];
    if (itemsArray.length > 1) {
        return `Are you sure you want to delete ${itemsArray.length} selected activities?`;
    }
    return `Are you sure you want to delete activity '${itemsArray[0]?.details}'?`;
}

export function getDeleteSuccessMessage(items) {
    const itemsArray = Array.isArray(items) ? items : [items];
    return itemsArray.length > 1
        ? `${itemsArray.length} activities deleted successfully`
        : "Activity deleted successfully";
}

export function resolveDuplicateActivityId(sourceActivityOrId) {
    return typeof sourceActivityOrId === "object"
        ? sourceActivityOrId?.activityId
        : sourceActivityOrId;
}

export function mergeDuplicateActivityData(apiData, sourceRow) {
    if (!sourceRow) return apiData || {};
    return {
        ...(apiData || {}),
        documentId: apiData?.documentId ?? sourceRow.documentId,
        documentName: apiData?.documentName ?? sourceRow.documentName,
        documentFile: apiData?.documentFile ?? sourceRow.documentFile,
        fileId: apiData?.fileId ?? sourceRow.fileId,
    };
}

export function truncateFileName(fileName) {
    if (!fileName) return;
    const extensionIndex = fileName.lastIndexOf(".");

    if (extensionIndex === -1) {
        return fileName.length > 8 ? fileName.substring(0, 18) : fileName;
    }

    const extension = fileName.substring(extensionIndex);

    if (fileName.length > 8) {
        return fileName.substring(0, 7 - extension.length) + extension;
    }

    return fileName;
}

export function getStatusClass(status) {
    switch (status) {
        case "Posted":
            return "bg-success";
        case "Rejected":
            return "bg-danger";
        default:
            return "bg-secondary";
    }
}

export function getActivitySubtypeStatus(activity) {
    if (
        activity?.subType === "ChitRequest" ||
        activity?.subType === "BillRequest"
    ) {
        return activity.subTypeStatus || "";
    }
    return "";
}

export function getRowStyle(dataItem, activityTypes = []) {
    const activityType = activityTypes.find(
        (type) => type.activityTypeID === dataItem.typeId,
    );

    if (
        activityType &&
        activityType.rowStyle &&
        typeof activityType.rowStyle === "string"
    ) {
        const match = activityType.rowStyle.match(/backgroundColor:['"]([^'"]+)['"]/);
        if (match?.[1]) {
            return { backgroundColor: match[1] };
        }
    }

    return {};
}

export function getSelectedDocumentIds(selectedState, listData = []) {
    return Object.keys(selectedState)
        .filter((key) => selectedState[key])
        .map((key) => {
            const selectedItem = listData.find((item) => item.activityId == key);
            return selectedItem?.documentId;
        })
        .filter((id) => id);
}

export function getSelectedDocumentsForShare(selectedState, listData = []) {
    return Object.keys(selectedState)
        .filter((key) => selectedState[key])
        .map((key) => {
            const selectedItem = listData.find((item) => item.activityId == key);
            return {
                id: selectedItem?.documentId,
                name: selectedItem?.documentName,
            };
        })
        .filter((item) => item.id && item.name);
}

export function buildMappedGridData(
    list,
    selectedState,
    idGetter,
    selectedField = ACTIVITY_SELECTED_FIELD,
) {
    return {
        data: list?.data?.map((item) => ({
            ...item,
            [selectedField]: selectedState[idGetter(item)],
        })),
        total: list?.total,
    };
}

export function isEmailFile(fileName = "") {
    const ext = fileName.split(".").pop()?.toLowerCase();
    return ext === "eml" || ext === "msg";
}

export function getEmailFileExt(fileName = "") {
    return fileName.split(".").pop()?.toLowerCase();
}

function normalizeMessageId(raw) {
    if (!raw) return null;
    let id = String(raw).trim();
    if (!id) return null;
    if (id.startsWith("<") && id.endsWith(">")) id = id.slice(1, -1);
    return `<${id}>`;
}

export function extractMessageIdFromEml(text = "") {
    if (!text) return null;
    const headersEnd = text.indexOf("\r\n\r\n");
    const headers = headersEnd === -1 ? text.slice(0, 8192) : text.slice(0, headersEnd);
    const unfolded = headers.replace(/\r?\n[\t ]+/g, " ");
    const match = unfolded.match(/^Message-ID\s*:\s*(.+?)\s*$/im);
    return match ? normalizeMessageId(match[1]) : null;
}

export async function readMessageIdFromEmlFile(file) {
    const slice = file.slice(0, 16384);
    const text = await slice.text();
    return extractMessageIdFromEml(text);
}

function decodeLatin1(bytes) {
    let out = "";
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
        out += String.fromCharCode.apply(
            null,
            bytes.subarray(i, Math.min(i + chunk, bytes.length)),
        );
    }
    return out;
}

function decodeUtf16Le(bytes) {
    let out = "";
    const chunk = 0x8000;
    for (let i = 0; i + 1 < bytes.length; i += chunk * 2) {
        const end = Math.min(i + chunk * 2, bytes.length - (bytes.length % 2));
        const codes = [];
        for (let j = i; j < end; j += 2) {
            codes.push(bytes[j] | (bytes[j + 1] << 8));
        }
        out += String.fromCharCode.apply(null, codes);
    }
    return out;
}

function findMessageIdInText(text) {
    const m = text.match(
        /(?:^|[\r\n\x00])Message-ID\s*:\s*<([^>\s\x00]*@[^>\s\x00]*)>/i,
    );
    if (m) return normalizeMessageId(m[1]);
    return null;
}

function findMessageIdByMapiTag(text) {
    const tagRe = /__substg1\.0_1035001[EF]/i;
    const tagMatch = tagRe.exec(text);
    if (!tagMatch) return null;
    const after = text.slice(tagMatch.index, tagMatch.index + 8192);
    const m = after.match(/<[^>\s\x00]{8,200}@[^>\s\x00]{1,200}>/);
    if (m) return normalizeMessageId(m[0]);
    return null;
}

function findMessageIdHeuristically(text) {
    const matches = [...text.matchAll(/<([^>\s\x00]{1,200}@[^>\s\x00]{1,200})>/g)];
    for (const m of matches) {
        const full = m[0];
        const local = m[1].split("@")[0];
        if (full.toLowerCase().includes("mailto:")) continue;
        if (local.length < 16) continue;
        if (!/\d/.test(local)) continue;
        return normalizeMessageId(full);
    }
    return null;
}

export async function readMessageIdFromMsgFile(file) {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const latin1 = decodeLatin1(bytes);
    const utf16 = decodeUtf16Le(bytes);
    return (
        findMessageIdInText(latin1) ||
        findMessageIdInText(utf16) ||
        findMessageIdByMapiTag(latin1) ||
        findMessageIdByMapiTag(utf16) ||
        findMessageIdHeuristically(latin1) ||
        findMessageIdHeuristically(utf16)
    );
}

async function detectEmailKind(file) {
    const name = (file.name || "").toLowerCase();
    if (name.endsWith(".eml")) return "eml";
    if (name.endsWith(".msg")) return "msg";
    try {
        const textHead = await file.slice(0, 4096).text();
        if (/^(?:[A-Za-z-]+\s*:\s*.*\r?\n)+(?:\r?\n|$)/.test(textHead)) {
            return "eml";
        }
    } catch {}
    return null;
}

export async function readMessageIdFromFile(file) {
    if (!file) return null;
    const kind = await detectEmailKind(file);
    if (kind === "eml") return await readMessageIdFromEmlFile(file);
    if (kind === "msg") return await readMessageIdFromMsgFile(file);
    return null;
}

async function extractHeaderFromEmlFile(file, headerName) {
    const slice = file.slice(0, 16384);
    const text = await slice.text();
    const headersEnd = text.indexOf("\r\n\r\n");
    const headers = headersEnd === -1 ? text : text.slice(0, headersEnd);
    const unfolded = headers.replace(/\r?\n[\t ]+/g, " ");
    const re = new RegExp(`^${headerName}\\s*:\\s*(.+?)\\s*$`, "im");
    const match = unfolded.match(re);
    return match ? match[1].trim() : null;
}

async function extractHeaderFromMsgFile(file, headerName) {
    try {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const re = new RegExp(
            `(?:^|[\\r\\n\\x00])${headerName}\\s*:\\s*([^\\r\\n\\x00]{1,500})`,
            "i",
        );
        for (const text of [decodeLatin1(bytes), decodeUtf16Le(bytes)]) {
            const m = text.match(re);
            if (m) {
                let value = m[1].trim();
                value = value.replace(/^=\?[^?]+\?[BbQq]\?[^?]+\?=\s*/g, "");
                if (value) return value;
            }
        }
        return null;
    } catch {
        return null;
    }
}

export async function extractEmailFromFromFile(file) {
    if (!file) return null;
    const kind = await detectEmailKind(file);
    if (kind === "eml") return extractHeaderFromEmlFile(file, "From");
    if (kind === "msg") return extractHeaderFromMsgFile(file, "From");
    return null;
}

export function extractEmailDomain(emailValue) {
    if (!emailValue) return null;
    const m = String(emailValue).match(/<?([^\s<>]+@[^\s<>]+)>?/);
    if (!m) return null;
    const addr = m[1];
    const at = addr.lastIndexOf("@");
    if (at === -1) return null;
    return addr.slice(at + 1).trim().toLowerCase();
}

export function domainsMatch(a, b) {
    if (!a || !b) return false;
    const aa = String(a).trim().toLowerCase();
    const bb = String(b).trim().toLowerCase();
    if (aa === bb) return true;
    return aa.endsWith("." + bb) || bb.endsWith("." + aa);
}

export function resolveEmailActivityType(activityTypes, fromValue, companyDomain) {
    if (!Array.isArray(activityTypes) || activityTypes.length === 0) return null;
    const fromDomain = extractEmailDomain(fromValue);
    const code = domainsMatch(fromDomain, companyDomain) ? "EO" : "EI";
    const found = activityTypes.find((t) => t.code === code);
    if (found) return found;
    return activityTypes.find((t) => t.code === "EO") || null;
}

export function parseDownloadFilename(response, asPDF = false) {
    let fileName;
    const contentDisposition = response.headers["content-disposition"];

    if (response.data?.filename) {
        fileName = response.data.filename;
    } else if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (fileNameMatch?.[1]) {
            fileName = fileNameMatch[1].replace(/['"]/g, "");
        }
    }
    if (!fileName) {
        fileName =
            (response.data?.fileID || "downloaded-document") + (asPDF ? ".pdf" : "");
    }
    return fileName;
}

export function downloadBlobAsFile(data, fileName) {
    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const tempLink = document.createElement("a");
    tempLink.href = url;
    tempLink.setAttribute("download", fileName);
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
    window.URL.revokeObjectURL(url);
}

export function applyActivitiesDragTransfer(
    event,
    dataItem,
    { listData = [], selectedState, idGetter, defaultMatterId },
) {
    const selectedItems = listData.filter((item) => selectedState[idGetter(item)]);

    const itemsToDrag =
        selectedItems.length > 0 && selectedState[idGetter(dataItem)]
            ? selectedItems
            : [dataItem];

    const selectedDocs = itemsToDrag
        .filter((item) => item.documentId)
        .map((item) => ({
            documentId: item.documentId,
            documentName: item.documentName || item.details,
            matterId: item.matterId || defaultMatterId,
            activityId: item.activityId,
            details: item.details,
        }));

    if (selectedDocs.length === 0) {
        event.preventDefault();
        return;
    }

    const payload = {
        documents: selectedDocs.map((d) => ({
            documentId: d.documentId,
            documentName: d.documentName,
            matterId: d.matterId,
            activityId: d.activityId,
        })),
    };

    event.dataTransfer.setData(
        "text/plain",
        "LINQ_CASE_ACTIVITIES:" + JSON.stringify(payload),
    );
    event.dataTransfer.effectAllowed = "copy";
}
